import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { ArrowLeft, Edit2, Printer, Loader2 } from 'lucide-react';
import { Division26DetailHeader } from './shared/Division26DetailHeader';
import { CircuitScheduleEditor } from './CircuitScheduleEditor';
import { panelFormConfig } from './panelConfig';
import { ConfirmDialog } from '../../components/ConfirmDialog';

const HEADER_FIELDS = panelFormConfig.fields.filter(f => f.key !== 'notes');
const fmtKva = (v) => v != null ? v.toFixed(1) : '-';

/**
 * Circuit Schedule Preview — read-only view with fullscreen edit modal.
 */
export const CircuitSchedulePreview = ({
  panel,
  projectId,
  manufacturers,
  onBack,
  editOpen,
  onEditOpen,
  onEditClose,
}) => {
  const [circuits, setCircuits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorDirty, setEditorDirty] = useState(false);
  const [unsavedConfirm, setUnsavedConfirm] = useState({ open: false });
  const printRef = useRef(null);

  const totalCircuits = panel?.circuits || 0;
  const totalRows = Math.ceil(totalCircuits / 2);

  const fetchCircuits = useCallback(async () => {
    if (!panel?.id || !projectId) return;
    try {
      const res = await axios.get(`${API}/electrical-panels/${projectId}/${panel.id}/circuits`);
      const saved = res.data;
      const slots = [];
      for (let i = 1; i <= totalCircuits; i++) {
        slots.push({ circuitNumber: i, deviceTag: '', equipmentTag: '', quantity: null, breakerTrip: '', poles: '', room: '', kvaLoad: null });
      }
      const map = {};
      saved.forEach(s => { map[s.circuitNumber] = s; });
      slots.forEach(slot => {
        const s = map[slot.circuitNumber];
        if (s) Object.assign(slot, {
          deviceTag: s.deviceTag || '',
          equipmentTag: s.equipmentTag || '',
          quantity: s.quantity ?? null,
          breakerTrip: s.breakerTrip || '',
          poles: s.poles || '',
          room: s.room || '',
          kvaLoad: s.kvaLoad ?? null,
        });
      });
      setCircuits(slots);
    } catch {
      const slots = [];
      for (let i = 1; i <= totalCircuits; i++) slots.push({ circuitNumber: i });
      setCircuits(slots);
    } finally {
      setLoading(false);
    }
  }, [panel?.id, projectId, totalCircuits]);

  useEffect(() => { fetchCircuits(); }, [fetchCircuits]);

  const rows = useMemo(() => {
    const result = [];
    for (let r = 0; r < totalRows; r++) {
      const lN = r * 2 + 1;
      const rN = r * 2 + 2;
      result.push({
        left: circuits.find(c => c.circuitNumber === lN) || { circuitNumber: lN },
        right: rN <= totalCircuits ? (circuits.find(c => c.circuitNumber === rN) || { circuitNumber: rN }) : null,
      });
    }
    return result;
  }, [circuits, totalRows, totalCircuits]);

  const totals = useMemo(() => {
    let single = 0;
    circuits.forEach(c => { single += c.kvaLoad || 0; });
    return { single };
  }, [circuits]);

  const filledCount = circuits.filter(c => c.deviceTag || c.equipmentTag || c.breakerTrip || c.poles).length;

  const cellCls = (filled) => filled ? 'text-slate-900' : 'text-slate-300';

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>${panel?.name} Circuit Schedule</title><style>
      body{font-family:system-ui,sans-serif;margin:20px;font-size:11px}
      table{border-collapse:collapse;width:100%} th,td{border:1px solid #ccc;padding:4px 6px;text-align:center}
      th{background:#f1f5f9;font-weight:600;text-transform:uppercase;font-size:10px}
      .spine{background:#475569;color:#fff;font-weight:700} .total{background:#f8fafc;font-weight:600}
      h2{margin:0 0 8px} .meta{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:12px;font-size:11px}
      .meta dt{color:#64748b;font-size:9px;text-transform:uppercase;font-weight:600} .meta dd{margin:2px 0 0;font-weight:500}
      @media print{body{margin:10px}}
    </style></head><body>`);
    win.document.write(`<h2>${panel?.name} — Circuit Schedule</h2>`);
    win.document.write(`<div class="meta">`);
    HEADER_FIELDS.forEach(f => {
      const val = f.type === 'manufacturer' ? (manufacturers.find(m => m.id === panel?.[f.key])?.name || panel?.manufacturerName || '-') : (panel?.[f.key] ?? '-');
      win.document.write(`<div><dt>${f.label}</dt><dd>${val}</dd></div>`);
    });
    win.document.write(`</div>`);
    win.document.write(content.innerHTML);
    win.document.write('</body></html>');
    win.document.close();
    win.print();
  };

  const handleEditorBack = () => {
    onEditClose();
    setEditorDirty(false);
    fetchCircuits(); // refresh data after edit
  };

  // Intercept modal close when editor has unsaved changes
  const handleModalClose = (open) => {
    if (!open) {
      if (editorDirty) {
        setUnsavedConfirm({
          open: true,
          title: 'Unsaved Changes',
          description: 'You have unsaved changes in the circuit schedule. If you close now, your changes will be lost. Are you sure?',
          confirmText: 'Discard Changes',
          variant: 'destructive',
          onConfirm: () => {
            setUnsavedConfirm({ open: false });
            handleEditorBack();
          },
        });
      } else {
        handleEditorBack();
      }
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  const renderLeftCell = (circ) => {
    const f = !!(circ.deviceTag || circ.equipmentTag || circ.breakerTrip);
    return (
      <>
        <td className={`px-2 py-2.5 text-left ${cellCls(f)}`}>{circ.room || '-'}</td>
        <td className={`px-1 py-2.5 text-left ${cellCls(f)}`}>{circ.deviceTag || '-'}</td>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{circ.equipmentTag || '-'}</td>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{circ.quantity != null ? circ.quantity : '-'}</td>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{fmtKva(circ.kvaLoad)}</td>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{circ.breakerTrip || '-'}</td>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{circ.poles || '-'}</td>
      </>
    );
  };

  const renderRightCell = (circ) => {
    if (!circ) { return <td colSpan={7} className="bg-slate-50/50" />; }
    const f = !!(circ.deviceTag || circ.equipmentTag || circ.breakerTrip);
    return (
      <>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{circ.poles || '-'}</td>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{circ.breakerTrip || '-'}</td>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{fmtKva(circ.kvaLoad)}</td>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{circ.quantity != null ? circ.quantity : '-'}</td>
        <td className={`px-1 py-2.5 text-center ${cellCls(f)}`}>{circ.equipmentTag || '-'}</td>
        <td className={`px-1 py-2.5 text-right ${cellCls(f)}`}>{circ.deviceTag || '-'}</td>
        <td className={`px-2 py-2.5 text-right ${cellCls(f)}`}>{circ.room || '-'}</td>
      </>
    );
  };

  return (
    <div data-testid="circuit-schedule-preview">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6">
        <Button size="sm" onClick={onBack} data-testid="circuit-preview-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1.5" />Back to Panel
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="circuit-preview-print-btn">
            <Printer className="h-4 w-4 mr-1.5" />Print
          </Button>
          <Button size="sm" onClick={onEditOpen} data-testid="circuit-preview-edit-btn">
            <Edit2 className="h-4 w-4 mr-1.5" />Edit
          </Button>
        </div>
      </div>

      {/* Panel Summary + QR */}
      <Division26DetailHeader item={panel} fields={HEADER_FIELDS} manufacturers={manufacturers} qrToken={panel?.qrToken} itemName={panel?.name} />

      {/* Info bar */}
      <div className="mt-3 mb-1 text-xs text-slate-500">{filledCount} of {totalCircuits} circuits configured</div>

      {/* Read-only Schedule Table */}
      <div ref={printRef} className="overflow-x-auto border rounded-lg overflow-hidden" data-testid="circuit-preview-grid">
        <table className="w-full border-collapse text-xs" style={{ minWidth: 1060 }}>
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-100 border-b">
              <th className="px-2 py-2.5 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Location</th>
              <th className="px-1 py-2.5 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Device Tag</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Equip Tag</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Qty</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">KVA</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Breaker</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Poles</th>
              <th className="px-1 py-2.5 text-center bg-slate-500 text-white font-bold text-xs">CKT</th>
              <th className="bg-slate-500" style={{ width: 18 }} />
              <th className="px-1 py-2.5 text-center bg-slate-500 text-white font-bold text-xs">CKT</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Poles</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Breaker</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">KVA</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Qty</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Equip Tag</th>
              <th className="px-1 py-2.5 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Device Tag</th>
              <th className="px-2 py-2.5 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Location</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className={`border-b border-slate-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                {renderLeftCell(row.left)}
                <td className="text-center font-bold bg-slate-500 text-white text-xs">{row.left.circuitNumber}</td>
                <td className="bg-slate-500">
                  <div className="flex justify-center">
                    <span className="w-1 h-2.5 rounded-sm bg-orange-400" />
                  </div>
                </td>
                <td className="text-center font-bold bg-slate-500 text-white text-xs">{row.right?.circuitNumber || ''}</td>
                {renderRightCell(row.right)}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold text-xs">
              <td colSpan={4} className="px-2 py-2.5 text-right text-slate-600">TOTAL</td>
              <td className="px-1 py-2.5 text-center text-slate-900">{totals.single.toFixed(1)}</td>
              <td /><td />
              <td colSpan={3} className="text-center bg-slate-500 text-orange-300 font-bold">
                {totals.single.toFixed(1)} KVA
              </td>
              <td /><td />
              <td className="px-1 py-2.5 text-center text-slate-900">{totals.single.toFixed(1)}</td>
              <td colSpan={4} className="px-2 py-2.5 text-left text-slate-600">TOTAL</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Fullscreen Edit Modal */}
      <Dialog open={editOpen} onOpenChange={handleModalClose}>
        <DialogContent className="max-w-[98vw] w-[98vw] max-h-[95vh] h-[95vh] flex flex-col p-0" data-testid="circuit-edit-fullscreen">
          <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
            <DialogTitle>Edit Circuit Schedule — {panel?.name}</DialogTitle>
            <DialogDescription>{totalCircuits} circuit slots</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <CircuitScheduleEditor
              panel={panel}
              projectId={projectId}
              manufacturers={manufacturers}
              onBack={handleEditorBack}
              onReview={() => {}}
              initialReviewOpen={false}
              embedded
              onSaveSuccess={handleEditorBack}
              onDirtyChange={setEditorDirty}
            />
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog {...unsavedConfirm} onClose={() => setUnsavedConfirm({ open: false })} />
    </div>
  );
};
